import {
  BulletFillIcon,
  ChevronDownOutlineIcon,
  ChevronUpOutlineIcon,
  CloseOutlineIcon,
} from '@applyboard/ui-icons';
import { css } from '@emotion/react';
import * as RadixPopover from '@radix-ui/react-popover';
import {
  useCombobox,
  useMultipleSelection,
  UseComboboxGetMenuPropsOptions,
  UseComboboxGetItemPropsOptions,
  UseComboboxGetToggleButtonPropsOptions,
  UseMultipleSelectionGetSelectedItemPropsOptions,
  UseComboboxGetInputPropsOptions,
  UseMultipleSelectionGetDropdownPropsOptions,
  UseComboboxStateChange,
  GetPropsCommonOptions,
} from 'downshift';
import isEqual from 'lodash/isEqual';
import React, { useState, useRef, RefObject } from 'react';

import { Chip, ChipProps } from '../Chip';
import { FieldCompatibleProps } from '../Field';
import { SrOnly } from '../SrOnly';
import { Text } from '../Text';
import { Intent, spacing, useFullTheme, IconType } from '~/design';
import { down, up } from '~/design/breakpoint';
import {
  InternalTextInput,
  InternalCheckbox,
  createInputColorUtil,
  InternalText,
  OptionLabel,
  IconWrapper,
} from '~/internal';
import { useId, useSelectStyling } from '~/utils';
import { svgVerticalAlign } from '~/utils/styling';
import { Flex } from '../Flex';
import { Box } from '../Box';

import {
  mapOptionsToGroups,
  NO_OPTIONS_ITEM,
  validateOptionIncluded,
} from './MultiCombobox.helpers';
import {
  FormControlContainer,
  FormControlContainerProps,
  useFormControlProps,
} from '~/internal/FormControlContainer';
import { flushSync } from 'react-dom';
import { useContextOrThrow } from '~/utils/useContextOrThrow';

/**
 * The MultiCombobox's option in the combo box drop down
 */
export interface OptionWithGroup {
  /** Disables the option in the MultiCombobox to be unselectable */
  disabled?: boolean;

  groupValue?: string;

  /** Visible text of the MultiCombobox option */
  label: string;

  /** MultiCombobox's value if the option is selected */
  value: string;
}

interface MultiComboboxContextProps {
  ariaLabel: MultiComboboxProps['aria-label'];
  closeMenu: () => void;
  disabled?: MultiComboboxProps['disabled'];
  filterOption?: (option: MultiComboboxOptionProps, query: string) => boolean;
  getDropdownProps: (
    options?: UseMultipleSelectionGetDropdownPropsOptions,
    extraOptions?: GetPropsCommonOptions,
  ) => any; // eslint-disable-line @typescript-eslint/no-explicit-any
  getInputProps: (
    options?: UseComboboxGetInputPropsOptions,
    otherOptions?: GetPropsCommonOptions,
  ) => any; // eslint-disable-line @typescript-eslint/no-explicit-any
  getItemProps: (
    options: UseComboboxGetItemPropsOptions<OptionWithGroup>,
  ) => Record<string, unknown>;
  getMenuProps: (
    options?: UseComboboxGetMenuPropsOptions,
    otherOptions?: GetPropsCommonOptions,
  ) => any; // eslint-disable-line @typescript-eslint/no-explicit-any
  getSelectedItemProps: (
    options: UseMultipleSelectionGetSelectedItemPropsOptions<OptionWithGroup>,
  ) => any; // eslint-disable-line @typescript-eslint/no-explicit-any
  getToggleButtonProps: (options?: UseComboboxGetToggleButtonPropsOptions) => any; // eslint-disable-line @typescript-eslint/no-explicit-any
  helpTextId: string;
  highlightedIndex: number | null;
  inputInsideMenu: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  inputValue: string;
  intent: MultiComboboxProps['intent'];
  invalid: MultiComboboxProps['invalid'];
  isOpen: boolean;
  label: MultiComboboxProps['label'];
  labelId: string;
  leadIcon: MultiComboboxProps['leadIcon'];
  loading: MultiComboboxProps['loading'];
  multiline?: MultiComboboxProps['multiline'];
  onPaste?: ({ preventDefault, text }: { preventDefault: () => void; text: string }) => void;
  openMenu: () => void;
  options: OptionWithGroup[];
  placeholder: MultiComboboxProps['placeholder'];
  removeSelectedItem: (item: OptionWithGroup) => void;
  required: MultiComboboxProps['required'];
  reset: () => void;
  selectedItem?: OptionWithGroup | null;
  selectedItems: OptionWithGroup[];
}

const MultiComboboxContext = React.createContext<MultiComboboxContextProps | null>(null);

export interface MultiComboboxProps extends FieldCompatibleProps<string[]> {
  /** The aria-label of the MultiCombobox */
  'aria-label'?: string;

  /** Can be either an array of MultiCombobox.Option(s) or MultiCombobox.OptionGroup(s) */
  children: React.ReactNode;

  /** If true, the MultiCombobox cannot be interacted with. */
  disabled?: boolean;

  /** Callback function to override the MultiCombobox default filter behavior.  The default behavior is to filter based on if the MultiCombobox.Option's label starts with the input value. */
  filterOption?: (option: OptionWithGroup, query: string) => boolean;

  /** The intent of the MultiCombobox. Maps to colour. */
  intent?: Intent;

  /** Determines how the label is placed relative to the input. (default: block) */
  labelPosition?: FormControlContainerProps['labelPosition'];

  /** The icon that appears on the left side of the MultiCombobox. */
  leadIcon?: IconType;

  /** If true, the MultiCombobox will show loading text when open. */
  loading?: boolean;

  /** How the dropdown menu will align with the trigger. `start` will align the start (left) of the
   * menu with the start of the trigger. `end` will align the end (right) of the menu with the end
   * of the trigger. `end` is useful when you have a MultiCombobox at the right side of the screen.
   * (default: start) */
  menuAlignment?: 'start' | 'end';

  /** Allows the MultiCombobox to consume more than 1 row */
  multiline?: boolean;

  /** Callback that is called when a user pastes text into the field */
  onPaste?: ({ preventDefault, text }: { preventDefault: () => void; text: string }) => void;

  /** Callback that is called when a user types into the field */
  onSearch?: (query: string) => void;

  /** The placeholder of the MultiCombobox. Placeholders are considered bad for accessibility, so it's *strongly encouraged* to avoid using this.
   * See this article for more details: https://www.smashingmagazine.com/2018/06/placeholder-attribute/ */
  placeholder?: string;

  placeholderVariant?: 'chips' | 'indicator';
}

/**
 * MultiCombobox allows the user to select multiple option and search for an option by typing in the MultiCombobox text input.
 * - If you want to alow only a single selected option, use the Combobox component.
 */
export function MultiCombobox({
  'aria-label': ariaLabel,
  children,
  disabled = false,
  filterOption,
  helpText,
  intent = 'secondary',
  invalid,
  label,
  labelPosition,
  leadIcon,
  loading = false,
  menuAlignment = 'start',
  multiline = false,
  onBlur,
  onChange,
  onPaste,
  onSearch,
  placeholder,
  placeholderVariant = 'chips',
  required,
  value,
}: MultiComboboxProps) {
  const { borderRadiuses, colors, elevations, spacing } = useFullTheme();
  const { adjustedIntent, helpTextId, labelId } = useFormControlProps({
    disabled,
    intent,
    invalid,
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const placeholderBtnRef = useRef<HTMLButtonElement>(null);
  const placeholderBtnId = useId();

  const options = mapOptionsToGroups(children);
  const [selectedItems, setSelectedItems] = useState(
    options.filter(item => value.includes(item.value)),
  );
  const [inputValue, setInputValue] = useState('');

  const filteredOptions = options.filter(option =>
    validateOptionIncluded(option, inputValue, filterOption),
  );

  const selectedItemsChanged = (selectedItems: OptionWithGroup[]) => {
    setSelectedItems(selectedItems);
    onChange(selectedItems?.map(item => item.value));
  };

  const anchorRef = React.useRef<HTMLDivElement>(null);
  const inputInsideMenu = placeholderVariant === 'indicator';

  const { getSelectedItemProps, getDropdownProps, removeSelectedItem, reset } =
    useMultipleSelection<OptionWithGroup>({
      getA11yRemovalMessage: options => {
        return `${options.removedSelectedItem.label} has been removed`;
      },
      selectedItems,
      onStateChange({ selectedItems: newSelectedItems, type }) {
        switch (type) {
          case useMultipleSelection.stateChangeTypes.FunctionReset:
            selectedItemsChanged([]);
            // when all chips are removed, the focus is never on the multicombobox trigger. if the
            // field is required, this might cause for it to not be validated.
            onBlur?.();
            break;
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
          case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
            selectedItemsChanged(newSelectedItems ? newSelectedItems : []);
            // when the menu is closed and the user is removing the chips, the
            // focus is never on the anchor/trigger element. so, when menu is closed and the last
            // chip was removed, emit onblur to properly validate the field when required
            if (!isOpen && newSelectedItems && newSelectedItems.length === 0) {
              onBlur?.();
            }
            break;
          default:
            break;
        }
      },
    });

  const [prevValue, setPrevValue] = React.useState(value);
  if (!isEqual(value, prevValue)) {
    setSelectedItems(
      options
        .filter(item => value.includes(item.value))
        .sort((a, b) => a.value.localeCompare(b.value)),
    );
    setPrevValue(value);
  }

  const {
    closeMenu,
    getInputProps,
    getItemProps,
    getLabelProps,
    getMenuProps,
    getToggleButtonProps,
    highlightedIndex,
    isOpen,
    openMenu,
    selectItem,
  } = useCombobox<OptionWithGroup>({
    inputValue,
    isItemDisabled(item) {
      return !!item.disabled;
    },
    items: filteredOptions,
    itemToString: item => (item ? item.label : ''),
    onInputValueChange: ({
      inputValue: newInputValue,
    }: UseComboboxStateChange<OptionWithGroup>) => {
      onSearch?.(newInputValue || '');
    },

    onStateChange: ({ inputValue, type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(inputValue || '');
          onSearch?.(inputValue || '');
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
          if (selectedItem) {
            selectedItems.some(item => item.value === selectedItem.value)
              ? selectedItemsChanged(
                  selectedItems?.filter(item => item.value !== selectedItem.value),
                )
              : selectedItemsChanged([...selectedItems, selectedItem]);
          }
          onSearch?.(inputValue || '');
          selectItem(null);

          if (type === useCombobox.stateChangeTypes.InputKeyDownEscape && inputInsideMenu) {
            placeholderBtnRef.current?.focus();
          }

          break;
        default:
          break;
      }
    },
    stateReducer(state, actionAndChanges) {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            highlightedIndex: state.highlightedIndex, // stay on the item after selecting it, otherwise user jumps to top of list
            isOpen: true, // keep the menu open after selection.
          };
        default:
          return changes;
      }
    },
  });

  if (!label && !ariaLabel) {
    throw new Error('One of label or aria-label is required');
  }

  return (
    <MultiComboboxContext.Provider
      value={{
        ariaLabel,
        closeMenu,
        disabled,
        filterOption,
        getDropdownProps,
        getInputProps,
        getItemProps,
        getMenuProps,
        getSelectedItemProps,
        getToggleButtonProps,
        helpTextId,
        highlightedIndex,
        inputInsideMenu,
        inputRef,
        inputValue,
        intent: adjustedIntent,
        invalid,
        isOpen,
        label,
        labelId,
        leadIcon,
        loading,
        multiline,
        openMenu,
        onPaste,
        options: filteredOptions,
        placeholder,
        removeSelectedItem: item => {
          if (selectedItems.length - 1 === 0) {
            inputRef.current?.focus();
          }
          removeSelectedItem(item);
        },
        required,
        reset,
        selectedItems,
      }}
    >
      <RadixPopover.Root open={isOpen}>
        <FormControlContainer
          disabled={disabled}
          helpText={helpText}
          helpTextId={helpTextId}
          intent={intent}
          invalid={invalid}
          label={label}
          labelId={labelId}
          labelPosition={labelPosition}
          required={required}
          {...getLabelProps(inputInsideMenu ? { htmlFor: placeholderBtnId } : {})}
        >
          <RadixPopover.Anchor
            ref={anchorRef}
            onBlur={() => {
              setTimeout(() => {
                // checks if the current element focus is not a child of the anchor/trigger element
                // if not, user is getting out of the component and it must emit onblur
                // we need a timeout because with keyboard there's a slight delay when tabbing through
                // elements, which focuses the body in between tabs.
                if (anchorRef.current && !anchorRef.current.contains(document.activeElement)) {
                  onBlur?.();
                }
              }, 0);
            }}
          >
            {inputInsideMenu ? (
              <ComboboxPlaceholder id={placeholderBtnId} ref={placeholderBtnRef} />
            ) : (
              <ComboboxChipsContent />
            )}
          </RadixPopover.Anchor>
        </FormControlContainer>

        <RadixPopover.Content
          align={menuAlignment ?? 'start'}
          aria-label="Combo box options"
          collisionPadding={8}
          css={{
            width: 'var(--radix-popper-anchor-width)',
            zIndex: 1,
          }}
          forceMount
        >
          <div
            css={{
              background: colors.bgRegular,
              borderRadius: borderRadiuses.control,
              boxShadow: elevations.z1,
              display: isOpen ? 'block' : 'none',
              flexDirection: 'column',
              listStyle: 'none',
              marginTop: '2px',
              maxHeight: 'var(--radix-popover-content-available-height)',
              minHeight:
                filteredOptions.length > 0
                  ? Math.min(filteredOptions.length * parseInt(spacing[12]), 200)
                  : spacing[12],
              minWidth: 300,
              overflow: 'auto',
              overflowY: 'auto',
              pointerEvents: 'all',
              position: 'absolute',
              width: '100%',
              zIndex: isOpen ? 1 : undefined, //make the zIndex primary when open
              '::-webkit-scrollbar': {
                backgroundColor: 'transparent',
                width: spacing[2],
              },
              '::-webkit-scrollbar-thumb': {
                backgroundColor: colors.borderRegularHover,
                borderRadius: borderRadiuses.container,
                marginRight: spacing[1],
              },
            }}
          >
            {inputInsideMenu ? (
              <MenuWithInput>{children}</MenuWithInput>
            ) : (
              <ComboboxMenu>{children}</ComboboxMenu>
            )}
          </div>
        </RadixPopover.Content>
      </RadixPopover.Root>
    </MultiComboboxContext.Provider>
  );
}

function SelectedChips() {
  const context = React.useContext(MultiComboboxContext);
  if (!context) {
    throw new Error('`SelectedChips` can only be rendered inside `MultiCombobox`');
  }

  const containerRef = useRef<HTMLDivElement>(null);

  const {
    disabled,
    getSelectedItemProps,
    multiline = false,
    removeSelectedItem,
    selectedItems,
  } = context;

  const [showAll, setShowAll] = useState(multiline);

  const defaultChipProps: ChipProps = {
    emphasis: 'outlined',
    size: 'xs',
  };
  const chipContainerStyle = css({
    whiteSpace: 'nowrap',
    flexShrink: 0,
  });

  const handleToggleShowAll = (value: boolean, offset: number) => {
    flushSync(() => {
      setShowAll(value);
    });
    const buttons = containerRef?.current?.querySelectorAll('button');
    buttons?.[buttons.length + offset]?.focus();
  };

  return (
    <div
      css={{
        alignContent: 'center',
        alignItems: multiline ? undefined : 'center',
        display: 'flex',
        flexFlow: multiline || showAll ? 'wrap' : 'no-wrap',
        flexGrow: 0,
        gap: spacing[2],
        margin: `${multiline || showAll ? spacing[3] : spacing[1]} 0`,
      }}
      ref={containerRef}
    >
      {showAll ? (
        <>
          {selectedItems.map((selectedItem, index) => (
            <div key={selectedItem.value} css={chipContainerStyle}>
              <InnerChip
                containerRef={containerRef}
                getSelectedItemProps={getSelectedItemProps}
                index={index}
                disabled={disabled}
                option={selectedItem}
                removeSelectedItem={() => removeSelectedItem(selectedItem)}
              />
            </div>
          ))}

          <div
            css={[
              chipContainerStyle,
              {
                [down('sm')]: {
                  display: selectedItems.length > 0 ? 'block' : 'none',
                },
                [up('md')]: {
                  display: 'none',
                },
              },
            ]}
          >
            <Chip {...defaultChipProps} onClick={() => handleToggleShowAll(false, -2)}>
              show less
            </Chip>
          </div>

          <div
            css={[
              chipContainerStyle,
              {
                display: 'none',
                [up('md')]: {
                  display: selectedItems.length > 1 ? 'block' : 'none',
                },
              },
            ]}
          >
            <Chip {...defaultChipProps} onClick={() => handleToggleShowAll(false, -1)}>
              show less
            </Chip>
          </div>
        </>
      ) : (
        <>
          {selectedItems.length > 0 && (
            /* TODO:  Fix this to calculate available width space: https://applyboard.atlassian.net/browse/UIE-1109 */
            <div
              css={[
                chipContainerStyle,
                {
                  display: 'none',
                  [up('md')]: { display: 'block' },
                },
              ]}
            >
              <InnerChip
                containerRef={containerRef}
                getSelectedItemProps={getSelectedItemProps}
                index={0}
                disabled={disabled}
                option={selectedItems[0]}
                removeSelectedItem={() => removeSelectedItem(selectedItems[0])}
              />
            </div>
          )}

          {/* TODO:  Fix this: https://applyboard.atlassian.net/browse/UIE-1109  */}

          {selectedItems.length > 0 && (
            <>
              <div
                css={[
                  chipContainerStyle,
                  {
                    [up('md')]: { display: 'none' },
                  },
                ]}
              >
                <Chip {...defaultChipProps} onClick={() => handleToggleShowAll(true, -2)}>
                  {`+ ${selectedItems.length}`}
                </Chip>
              </div>

              {selectedItems.length > 1 && (
                <div
                  css={[
                    chipContainerStyle,
                    {
                      display: 'none',
                      [up('md')]: {
                        display: 'block',
                      },
                    },
                  ]}
                >
                  <Chip {...defaultChipProps} onClick={() => handleToggleShowAll(true, -1)}>
                    {`+ ${selectedItems.length - 1}`}
                  </Chip>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

interface InnerChipProps {
  containerRef: RefObject<HTMLDivElement>;
  disabled?: boolean;
  getSelectedItemProps: (
    options: UseMultipleSelectionGetSelectedItemPropsOptions<OptionWithGroup>,
    //eslint-disable-next-line
  ) => any; //Downshift not typed properly
  index: number;
  option: OptionWithGroup;
  removeSelectedItem: (item: OptionWithGroup) => void;
}

function InnerChip({
  containerRef,
  disabled,
  getSelectedItemProps,
  index,
  option,
  removeSelectedItem,
}: InnerChipProps) {
  return (
    <Chip
      emphasis="outlined"
      size="xs"
      {...getSelectedItemProps({ selectedItem: option, index })}
      disabled={disabled}
      key={option.value}
      onClick={undefined}
      onDelete={() => {
        flushSync(() => {
          removeSelectedItem(option);
        });
        // `buttons` includes each chip itself, each chip's delete button, two of the "show more/less"
        // button, so it's = 2 * the number of chips + 2
        const buttons = containerRef?.current?.querySelectorAll('button');
        if (!buttons || buttons.length === 0) {
          return;
        }
        const numChipButtons = buttons.length - 2;
        const actualIndex = 2 * index;
        const indexToUse = actualIndex >= numChipButtons ? actualIndex - 2 : actualIndex;
        buttons?.[indexToUse]?.focus();
      }}
    >
      {option.label}
    </Chip>
  );
}

function ComboboxInput() {
  const context = React.useContext(MultiComboboxContext);
  if (!context) {
    throw new Error('`ComboboxInput` can only be rendered inside `MultiCombobox`');
  }
  const {
    ariaLabel,
    closeMenu,
    disabled,
    getDropdownProps,
    getInputProps,
    inputInsideMenu,
    inputRef,
    isOpen,
    onPaste,
    openMenu,
    placeholder,
    required,
  } = context;

  return (
    <div
      css={[
        {
          flexGrow: 1,
        },
      ]}
    >
      <InternalTextInput
        ref={inputRef}
        {...getInputProps({
          ...getDropdownProps({
            'aria-label': ariaLabel,
            preventKeyAction: isOpen,
            ref: inputRef,
          }),
          onKeyDown: e => {
            if (inputInsideMenu && e.key === 'Tab') {
              // For some reason, the popover captures the tab?
              closeMenu();
            }
          },
        })}
        borders={inputInsideMenu}
        disabled={disabled}
        emphasis="outlined"
        onFocus={openMenu} //this version of Downshift does not have the onFocus in the getInputProps
        onPaste={e => {
          onPaste?.({
            preventDefault: () => e.preventDefault(),
            text: e.clipboardData.getData('text'),
          });
        }}
        placeholder={placeholder}
        required={required}
        size="sm"
        type="text"
      />
    </div>
  );
}

function ComboboxToggle() {
  const context = React.useContext(MultiComboboxContext);
  if (!context) {
    throw new Error('`ComboboxToggle` can only be rendered inside `MultiCombobox`');
  }
  const {
    disabled = false,
    intent = 'secondary',
    isOpen,
    selectedItems = [],
    reset,
    getToggleButtonProps,
  } = context;
  return (
    <div
      css={{
        alignSelf: 'flex-start',
        display: 'flex',
        flexWrap: 'nowrap',
        gap: spacing[4],
        marginTop: spacing[3],
      }}
    >
      {selectedItems.length > 0 && (
        <button
          aria-label="Deselect all selected options"
          css={{
            alignSelf: 'center',
            backgroundColor: 'transparent',
            borderStyle: 'none',
            height: spacing[6],
            lineHeight: 0,
            margin: '0px',
            padding: '0px',
            width: spacing[6],
          }}
          onClick={() => reset()}
          type="button"
        >
          <IconWrapper contrast="high" disabled={disabled} intent={intent} selector="button">
            <CloseOutlineIcon />
          </IconWrapper>
        </button>
      )}
      <button
        css={{
          backgroundColor: 'transparent',
          borderStyle: 'none',
          cursor: disabled ? 'auto' : 'pointer',
          height: spacing[6],
          lineHeight: 0,
          margin: '0px',
          padding: '0px',
          width: spacing[6],
        }}
        type="button"
        {...getToggleButtonProps()} //Downshift injected events and props
        aria-label="toggle menu"
      >
        <IconWrapper contrast="high" disabled={disabled} intent={intent} selector="button">
          {isOpen ? <ChevronUpOutlineIcon /> : <ChevronDownOutlineIcon />}
        </IconWrapper>
      </button>
    </div>
  );
}

function ComboboxChipsContent() {
  const context = React.useContext(MultiComboboxContext);
  const theme = useFullTheme();
  if (!context) {
    throw new Error('`ComboboxChipsContent` can only be rendered inside `MultiCombobox`');
  }
  const { spacing, borderRadiuses } = theme;
  const colorUtil = createInputColorUtil(theme);
  const { disabled, intent, leadIcon: LeadIcon, multiline } = context;

  return (
    <div
      css={{
        alignItems: 'center',
        borderColor: colorUtil('border', intent ?? 'secondary'),
        borderRadius: borderRadiuses.control,
        borderStyle: 'solid',
        borderWidth: spacing['1px'],
        display: 'flex',
        flexDirection: 'row',
        flexGrow: 1,
        gap: spacing[4],
        margin: 0,
        minHeight: spacing[12],
        paddingLeft: spacing[4],
        paddingRight: spacing[4],
        ':focus-within': {
          outlineWidth: theme.spacing['2px'],
          outlineStyle: 'solid',
          outlineColor: colorUtil('outline', intent ?? 'secondary'),
          borderColor: 'transparent',
        },
      }}
    >
      {LeadIcon && (
        <div
          css={[
            {
              alignSelf: 'flex-start',
              marginTop: spacing[3],
            },
            svgVerticalAlign,
          ]}
        >
          <LeadIcon contrast={disabled ? 'mid' : 'high'} intent={intent} />
        </div>
      )}

      <div
        css={{
          display: 'inline-flex',
          flexFlow: multiline ? 'wrap' : undefined,
          width: '100%',
        }}
      >
        <SelectedChips />
        <ComboboxInput />
      </div>

      {!disabled && <ComboboxToggle />}
    </div>
  );
}

function ComboboxMenu({ children }: { children: MultiComboboxProps['children'] }) {
  const { getMenuProps, isOpen, labelId, loading, options } = useContextOrThrow(
    MultiComboboxContext,
    '`ComboboxMenu` can only be rendered inside `MultiCombobox`',
  );
  const { colors } = useFullTheme();

  return (
    <ul
      css={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
      }}
      {...getMenuProps({
        'aria-labelledby': labelId,
      })}
    >
      {isOpen &&
        (loading ? (
          <li
            css={{
              alignItems: 'center',
              background: colors.bgRegular,
              cursor: 'not-allowed',
              listStyleType: 'none',
              padding: `0 ${spacing[4]}`,
            }}
          >
            <EmptyOption>Loading...</EmptyOption>
          </li>
        ) : options.length > 0 ? (
          children
        ) : (
          <MultiCombobox.Option {...NO_OPTIONS_ITEM} />
        ))}
    </ul>
  );
}

export interface ComboboxOptionsGroupProps {
  children: React.ReactNode;
  groupValue: string;
  label: string;
}

MultiCombobox.OptionGroup = function ComboboxOptionGroup({
  children,
  groupValue,
  label,
}: ComboboxOptionsGroupProps) {
  const context = React.useContext(MultiComboboxContext);
  const theme = useFullTheme();
  const { spacing } = theme;

  if (!context) {
    throw new Error('`MultiCombobox.OptionGroup` can only be rendered inside `MultiCombobox`');
  }

  if (
    React.Children.toArray(children).filter(
      child => child instanceof Object && 'type' in child && child.type === MultiCombobox.Option,
    ).length !== React.Children.count(children)
  ) {
    throw new Error('MultiCombobox.OptionGroup only allows children of type MultiCombobox.Option');
  }

  // Determines if we should show the group labels if
  // - there is no input value then all sections are shown
  // - Otherwise, there must be at least one option in its group that is visible.
  const visible = !context.inputValue
    ? true
    : context.options.some(
        option =>
          option.groupValue === groupValue &&
          validateOptionIncluded(option, context.inputValue, context.filterOption),
      );

  if (visible) {
    return (
      <li>
        <ul
          role="group"
          aria-labelledby={groupValue}
          css={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
          }}
        >
          <li
            role="presentation"
            id={groupValue}
            css={{
              display: 'list-item',
              listStyleType: 'none',
              paddingLeft: spacing[4],
              paddingTop: spacing[2],
              paddingBottom: spacing[2],
              paddingRight: spacing[4],
              alignItems: 'center',
              borderRadius: theme.borderRadiuses.control,
              cursor: 'auto',
            }}
          >
            <Text variant="labelM">{label}</Text>
          </li>
          {children}
        </ul>
      </li>
    );
  }
  return null;
};

export interface MultiComboboxOptionProps {
  disabled?: boolean;
  label: string;
  value: string;
}

MultiCombobox.Option = function MultiComboboxOption({
  disabled = false,
  label,
  value,
}: MultiComboboxOptionProps) {
  const theme = useFullTheme();
  const { spacing } = theme;
  const context = React.useContext(MultiComboboxContext);

  if (!context) {
    throw new Error('`MultiCombobox.Option` can only be rendered inside `MultiCombobox`');
  }

  const { filterOption, getItemProps, highlightedIndex, inputValue, options, selectedItems } =
    context;

  const noOptionsAvailable = options.length === 0;

  const currentOption = noOptionsAvailable
    ? NO_OPTIONS_ITEM
    : options.find(option => option.value === value);
  const visible = noOptionsAvailable
    ? true
    : currentOption
      ? validateOptionIncluded(currentOption, inputValue, filterOption)
      : false;

  if (visible && currentOption) {
    const selected = selectedItems ? selectedItems.map(item => item.value).includes(value) : false;

    //Determines if the this option should be highlighted
    const currentIndex = options.findIndex(option => option.value === currentOption.value);

    return (
      <li
        css={{
          alignItems: 'center',
          background: noOptionsAvailable
            ? theme.colors.bgRegular
            : highlightedIndex === currentIndex
              ? theme.colors.bgRegularHover
              : theme.colors.bgRegular,
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: visible ? 'list-item' : 'none',
          listStyleType: 'none',
          padding: `0 ${spacing[4]}`,
        }}
        {...getItemProps({
          'aria-selected': selected,
          index: currentIndex,
          item: currentOption,
        })}
      >
        {noOptionsAvailable ? (
          <EmptyOption>{label}</EmptyOption>
        ) : (
          <InternalCheckbox
            checked={
              selectedItems
                ? selectedItems.some(selectedItem => selectedItem.value === value)
                : false
            }
            disabled={disabled}
            label={
              <>
                <OptionLabel
                  label={label}
                  search={inputValue && !filterOption ? inputValue : undefined}
                />
                <SrOnly>{label}</SrOnly>
              </>
            }
            nonInteractive
            onChange={() => null}
          />
        )}
      </li>
    );
  }
  return null;
};

function EmptyOption({ children }: { children: string }) {
  return (
    <div
      css={{
        alignItems: 'center',
        display: 'flex',
        minHeight: spacing[12],
      }}
    >
      <InternalText variant="bodyM" contrast="low">
        {children}
      </InternalText>
    </div>
  );
}

function MenuWithInput({ children }: { children: MultiComboboxProps['children'] }) {
  const context = React.useContext(MultiComboboxContext);
  if (!context) {
    throw new Error('`MenuWithInput` can only be rendered inside `MultiCombobox`');
  }
  return (
    <Flex direction="column" gap={2}>
      <Box px={2} pt={2}>
        <ComboboxInput />
      </Box>

      <ComboboxMenu>{children}</ComboboxMenu>
    </Flex>
  );
}

const ComboboxPlaceholder = React.forwardRef<HTMLButtonElement, { id: string }>(
  function ComboboxPlaceholder({ id }, ref) {
    return (
      <RadixPopover.Trigger asChild ref={ref}>
        {/* @ts-expect-error The `asChild` prop will pass most of the props we need */}
        <ComboboxPlaceholderInner id={id} />
      </RadixPopover.Trigger>
    );
  },
);

interface ComboboxPlaceholderInnerProps {
  'aria-controls': string;
  'aria-expanded': boolean;
  'aria-haspopup': 'dialog';
  'data-state': string;
  id: string;
}

const ComboboxPlaceholderInner = React.forwardRef<HTMLButtonElement, ComboboxPlaceholderInnerProps>(
  function ComboboxPlaceholderInner({ id, ...props }, ref) {
    const context = React.useContext(MultiComboboxContext);
    const { spacing } = useFullTheme();
    if (!context) {
      throw new Error('`ComboboxPlaceholderInner` can only be rendered inside `MultiCombobox`');
    }
    const {
      disabled,
      getToggleButtonProps,
      intent,
      invalid,
      isOpen,
      placeholder: _placeholder,
      selectedItems,
    } = context;
    const placeholder = _placeholder || 'Select...';
    const buttonStyle = useSelectStyling({ emphasis: 'outlined', invalid, intent });

    return (
      <button
        css={[buttonStyle, { width: '100%' }]}
        {...getToggleButtonProps({ ...props, disabled, id, ref, tabIndex: 0 })}
        type="button"
      >
        <div
          css={{
            alignItems: 'center',
            display: 'flex',
            gap: spacing[1],
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {placeholder}
          <Flex>
            {selectedItems.length > 0 && (
              <IconWrapper disabled={disabled} intent="primary" selector="button">
                <BulletFillIcon />
              </IconWrapper>
            )}
            <IconWrapper
              contrast="high"
              disabled={disabled}
              intent={intent ?? 'secondary'}
              selector="button"
            >
              {isOpen ? <ChevronUpOutlineIcon /> : <ChevronDownOutlineIcon />}
            </IconWrapper>
          </Flex>
        </div>
      </button>
    );
  },
);
